import {
  call,
  put,
  select,
  takeLatest,
  delay,
  fork,
  cancel,
  race,
  take,
  cancelled,
} from 'redux-saga/effects';
import { learningApi } from '../../../api.learning';
import { generativeAi } from '../../../api';
import { equals } from 'ramda';
import {
  v4 as uuid,
  validate as validUuid,
  version as uuidVersion,
} from 'uuid';

const requestError = (error, fallback) => ({
  ...error,
  message:
    (error.json && (error.json.error || error.json.message)) ||
    error.message ||
    fallback,
  status: error.response ? error.response.status : error.status,
});

const readSetupRequest = (key) => {
  const stored = sessionStorage.getItem(`tc1683-learning-setup:${key}`);
  if (stored === null) return null;
  const body = JSON.parse(stored);
  if (
    !body ||
    Array.isArray(body) ||
    Object.keys(body).length !== 4 ||
    Object.keys(body).some(
      (field) =>
        ![
          'setupId',
          'expectedGeneration',
          'reason',
          'learningEnabled',
        ].includes(field)
    ) ||
    typeof body.setupId !== 'string' ||
    body.setupId !== body.setupId.toLowerCase() ||
    !validUuid(body.setupId) ||
    uuidVersion(body.setupId) !== 4 ||
    !Number.isSafeInteger(body.expectedGeneration) ||
    body.expectedGeneration < 0 ||
    typeof body.reason !== 'string' ||
    !body.reason.trim() ||
    body.reason.length > 4000 ||
    body.learningEnabled !== true
  )
    throw new Error('Invalid retained setup request');
  return body;
};

const readRecoveryRequest = (key, companyId) => {
  const stored = sessionStorage.getItem(`tc1683-learning-recovery:${key}`);
  if (stored === null) return null;
  const body = JSON.parse(stored);
  if (
    !body ||
    Array.isArray(body) ||
    Object.keys(body).length !== 3 ||
    Object.keys(body).some(
      (field) => !['companyId', 'recoveryId', 'reason'].includes(field)
    ) ||
    body.companyId !== companyId ||
    typeof body.recoveryId !== 'string' ||
    body.recoveryId !== body.recoveryId.toLowerCase() ||
    !validUuid(body.recoveryId) ||
    uuidVersion(body.recoveryId) !== 4 ||
    typeof body.reason !== 'string' ||
    !body.reason.trim() ||
    body.reason.length > 4000
  )
    throw new Error('Invalid retained recovery request');
  return body;
};

const validRecoveryScope = (scope) => {
  if (!scope || typeof scope !== 'object' || Array.isArray(scope)) return false;
  if (scope.type === 'company') return Object.keys(scope).length === 1;
  if (
    !['integration', 'bookingAgent'].includes(scope.type) ||
    typeof scope.integrationConnectionId !== 'string' ||
    !scope.integrationConnectionId.trim()
  )
    return false;
  return scope.type === 'integration'
    ? Object.keys(scope).length === 2
    : Object.keys(scope).length === 3 &&
        typeof scope.bookingAgentId === 'string' &&
        Boolean(scope.bookingAgentId.trim());
};

const validRecovery = (action, status, companyId, operationId, recoveryId) =>
  Boolean(
    status &&
      action &&
      action.authorizationKind === 'recoveryReplay' &&
      action.action === 'replay' &&
      !action.syntheticPreview &&
      !action.readOnly &&
      ['user', 'configured-admin'].includes(action.operatorKind) &&
      typeof action.originalStaffUserId === 'string' &&
      action.originalStaffUserId &&
      action.actorId === status.actorId &&
      action.companyId === companyId &&
      action.environment === status.environment &&
      action.preparedActionId === recoveryId &&
      ['prepared', 'committing', 'committed'].includes(action.status) &&
      action.payload &&
      typeof action.payload === 'object' &&
      !Array.isArray(action.payload) &&
      action.payload.operationId === operationId &&
      action.payload.action === action.action &&
      action.payload.expectedGeneration === action.expectedGeneration &&
      equals(action.payload.scope, action.scope) &&
      equals(action.payload.expectedRevisions, action.expectedRevisions) &&
      equals(action.payload.diff, action.diff) &&
      !('_recoveryAuthority' in action.payload) &&
      !('_preparationAuthority' in action.payload) &&
      typeof action.actionDigest === 'string' &&
      /^[a-f0-9]{64}$/.test(action.actionDigest) &&
      action.actionDigest === action.payloadDigest &&
      validRecoveryScope(action.scope) &&
      Array.isArray(action.expectedRevisions) &&
      Array.isArray(action.diff) &&
      Number.isSafeInteger(action.expectedGeneration) &&
      action.expectedGeneration >= 0 &&
      new Date(action.expiresAt).getTime() > Date.now()
  );

function* performLearningRequest({ type, payload }, signal) {
  if (type === 'learning/FILTERS') return;
  if (
    type === 'learning/ENTER' ||
    type === 'learning/SIGNED_OUT' ||
    type === 'learning/READ_ACCESS_DENIED' ||
    type === 'learning/END_VERIFICATION'
  ) {
    yield call(learningApi.signOut);
    return;
  }
  const state = yield select((root) => root.generativeAi.learning);
  const companyId = state.companyId;
  const isAdmin = yield select(
    (root) => root.account && root.account.isAnAdmin
  );
  if (!isAdmin) {
    yield call(learningApi.signOut);
    yield put({ type: 'learning/SIGNED_OUT', companyId });
    return;
  }
  let currentStatus = state.status;
  yield put({
    type: 'learning/STARTED',
    companyId,
    payload: { history: type === 'learning/LOAD_HISTORY' },
  });
  if (
    [
      'learning/LOAD_HISTORY',
      'learning/LOAD_ACTION',
      'learning/PREPARE',
      'learning/OPEN_RESOLUTION',
      'learning/CONFIRM',
      'learning/IMPORT_SKILL',
      'learning/PREPARE_MIGRATION',
      'learning/REPLACE_IMPORTED_SKILL',
      'learning/PREPARE_RECOVERY',
      'learning/CONFIRM_RECOVERY',
    ].includes(type)
  ) {
    const status = yield call(
      generativeAi.getLearningStatus,
      companyId,
      signal
    );
    if (status.error) {
      yield call(learningApi.signOut);
      yield put({
        type: [401, 403].includes(requestError(status.error).status)
          ? 'learning/READ_ACCESS_DENIED'
          : 'learning/ERROR',
        payload: {
          ...requestError(status.error, 'Unable to load learning status. Please retry.'),
          history: type === 'learning/LOAD_HISTORY',
        },
        companyId,
      });
      return;
    }
    yield put({
      type: 'learning/STATUS_LOADED',
      payload: status.response,
      companyId,
    });
    currentStatus = status.response;
    if (currentStatus.actorId) {
      const key = `${companyId}:${currentStatus.actorId}`;
      try {
        const body = readSetupRequest(key);
        const attempt = state.setupAttempts[key];
        if (
          body &&
          attempt &&
          attempt.body &&
          JSON.stringify(body) !== JSON.stringify(attempt.body)
        )
          throw new Error('Retained setup request changed');
        if (body)
          yield put({
            type: 'learning/SETUP_STORED',
            companyId,
            payload: { key, body },
          });
      } catch (error) {
        yield put({
          type: 'learning/SETUP_STORAGE_ERROR',
          companyId,
          payload: { key },
        });
      }
    }
  }
  const selectedItem = state.items.find(
    (item) => payload && item.operationId === payload.operationId
  );
  if (
    [
      'learning/PREPARE',
      'learning/OPEN_RESOLUTION',
      'learning/CONFIRM',
      'learning/LOAD_ACTION',
      'learning/IMPORT_SKILL',
      'learning/PREPARE_MIGRATION',
      'learning/REPLACE_IMPORTED_SKILL',
    ].includes(type) &&
    (!currentStatus ||
      currentStatus.publisherEnabled === false ||
      state.filters.syntheticPreview ||
      state.syntheticPreview ||
      (selectedItem &&
        (selectedItem.syntheticPreview || selectedItem.readOnly)) ||
      (state.action &&
        (state.action.syntheticPreview || state.action.readOnly)) ||
      (type === 'learning/LOAD_ACTION' &&
        typeof payload === 'string' &&
        payload.startsWith('tc1683-preview-')))
  ) {
    yield put({
      type: 'learning/ERROR',
      companyId,
      payload: {
        message:
          'Preparation and confirmation are unavailable while publication is off or synthetic examples are displayed.',
      },
    });
    return;
  }
  if (
    type === 'learning/PREPARE_RECOVERY' ||
    type === 'learning/CONFIRM_RECOVERY'
  ) {
    const operationId =
      type === 'learning/PREPARE_RECOVERY'
        ? payload.operationId
        : state.recovery && state.recovery.operationId;
    const selected = state.items.find(
      (item) => item.operationId === operationId
    );
    const deny = (message) => ({
      type: 'learning/ERROR',
      companyId,
      payload: { message, recoveryAction: true },
    });
    if (
      !currentStatus ||
      !currentStatus.actorId ||
      !currentStatus.environment ||
      currentStatus.publisherEnabled !== true ||
      currentStatus.learningEnabled !== true ||
      state.filters.syntheticPreview ||
      state.syntheticPreview ||
      !selected ||
      selected.syntheticPreview ||
      selected.readOnly ||
      !['held', 'exhausted'].includes(selected.status)
    ) {
      yield put(
        deny(
          'Replay review is unavailable for this operation or current learning state.'
        )
      );
      return;
    }
    let response;
    let body;
    const key = `${companyId}:${currentStatus.actorId}:${operationId}`;
    if (type === 'learning/PREPARE_RECOVERY') {
      try {
        body = readRecoveryRequest(key, companyId);
        if (!body) {
          if (
            !payload.reason ||
            typeof payload.reason !== 'string' ||
            !payload.reason.trim() ||
            payload.reason.length > 4000
          )
            throw new Error('Enter a recovery reason');
          body = { companyId, recoveryId: uuid(), reason: payload.reason };
        }
        const stored = state.recoveryRequests[key];
        if (stored && JSON.stringify(stored) !== JSON.stringify(body))
          throw new Error('Retained recovery request changed');
        const serialized = JSON.stringify(body);
        sessionStorage.setItem(`tc1683-learning-recovery:${key}`, serialized);
        if (
          sessionStorage.getItem(`tc1683-learning-recovery:${key}`) !==
          serialized
        )
          throw new Error('Recovery request could not be saved');
      } catch (error) {
        yield put(
          deny(
            'Recovery request could not be safely retained. No further recovery request will be sent.'
          )
        );
        return;
      }
      yield put({
        type: 'learning/RECOVERY_REQUEST_STORED',
        companyId,
        payload: { key, body },
      });
      response = yield call(
        generativeAi.prepareLearningRecovery,
        operationId,
        body
      );
    } else {
      const recovery = state.recovery;
      body = state.recoveryRequests[key];
      if (
        !recovery ||
        !body ||
        !validRecovery(
          recovery.action,
          currentStatus,
          companyId,
          operationId,
          body.recoveryId
        )
      ) {
        yield put(
          deny('Review the exact current recovery action before confirming.')
        );
        return;
      }
      response = yield call(
        generativeAi.getLearningRecovery,
        body.recoveryId,
        companyId
      );
      if (
        !response.error &&
        (!validRecovery(
          response.response,
          currentStatus,
          companyId,
          operationId,
          body.recoveryId
        ) ||
          response.response.actionDigest !== recovery.action.actionDigest ||
          response.response.originalStaffUserId !==
            recovery.action.originalStaffUserId ||
          response.response.operatorKind !== recovery.action.operatorKind)
      ) {
        yield put(
          deny(
            'The recovery action changed or expired. Confirmation was not sent.'
          )
        );
        return;
      }
      if (!response.error)
        response = yield call(
          generativeAi.confirmLearningRecovery,
          body.recoveryId,
          recovery.action.actionDigest,
          companyId
        );
    }
    if (response.error) {
      yield put(
        deny(
          response.error.json
            ? response.error.json.error || response.error.json.message
            : response.error.message
        )
      );
      return;
    }
    if (type === 'learning/PREPARE_RECOVERY') {
      if (
        !validRecovery(
          response.response,
          currentStatus,
          companyId,
          operationId,
          body.recoveryId
        )
      ) {
        yield put(
          deny(
            'The service returned an incomplete or mismatched recovery action.'
          )
        );
        return;
      }
      yield put({
        type: 'learning/RECOVERY_PREPARED',
        companyId,
        payload: { operationId, action: response.response },
      });
    } else {
      yield put({
        type: 'learning/RECOVERY_CONFIRMED',
        companyId,
        payload: response.response,
      });
      yield put({ type: 'learning/LOAD_HISTORY', companyId });
    }
    return;
  }
  let result;
  let success;
  switch (type) {
    case 'learning/SETUP': {
      const key = currentStatus && `${companyId}:${currentStatus.actorId}`;
      const attempt = key && state.setupAttempts[key];
      const initialSetup = currentStatus && currentStatus.initialSetup;
      if (
        !currentStatus ||
        !currentStatus.actorId ||
        currentStatus.publisherEnabled !== true ||
        state.filters.syntheticPreview ||
        state.syntheticPreview ||
        state.items.some((item) => item.syntheticPreview || item.readOnly) ||
        (attempt && (attempt.result || attempt.recoveryError)) ||
        (!attempt &&
          (!initialSetup ||
            initialSetup.available !== true ||
            !Number.isSafeInteger(initialSetup.expectedGeneration) ||
            initialSetup.expectedGeneration < 0 ||
            typeof payload !== 'string' ||
            !payload.trim() ||
            payload.length > 4000))
      ) {
        yield put({
          type: 'learning/ERROR',
          companyId,
          payload: {
            message:
              'Initial setup is unavailable in this context. An existing request must be retried unchanged.',
          },
        });
        return;
      }
      const body = attempt
        ? attempt.body
        : {
            setupId: uuid(),
            expectedGeneration: initialSetup.expectedGeneration,
            reason: payload,
            learningEnabled: true,
          };
      try {
        const stored = readSetupRequest(key);
        if (
          stored &&
          (!attempt || JSON.stringify(stored) !== JSON.stringify(body))
        )
          throw new Error('Review the retained request first');
        const serialized = JSON.stringify(body);
        sessionStorage.setItem(`tc1683-learning-setup:${key}`, serialized);
        if (
          sessionStorage.getItem(`tc1683-learning-setup:${key}`) !== serialized
        )
          throw new Error('Setup request could not be retained');
      } catch (error) {
        yield put({
          type: 'learning/SETUP_STORAGE_ERROR',
          companyId,
          payload: { key },
        });
        return;
      }
      if (!attempt)
        yield put({
          type: 'learning/SETUP_STORED',
          companyId,
          payload: { key, body },
        });
      result = yield call(generativeAi.setupLearning, companyId, body);
      if (result.error) {
        yield put({
          type: 'learning/ERROR',
          companyId,
          payload: {
            setupAction: true,
            message: result.error.json
              ? result.error.json.error || result.error.json.message
              : result.error.message,
            status: result.error.response
              ? result.error.response.status
              : result.error.status,
          },
        });
      } else {
        yield put({
          type: 'learning/SETUP_RESULT',
          companyId,
          payload: { key, result: result.response },
        });
        yield put({ type: 'learning/LOAD_HISTORY', companyId });
      }
      return;
    }
    case 'learning/LOAD_HISTORY': {
      result = yield call(generativeAi.getLearningHistory, {
        ...state.filters,
        view: 'summary',
        hours: state.filters.hours === null ? undefined : state.filters.hours,
        syntheticPreview: state.filters.syntheticPreview ? true : undefined,
        companyId,
        limit: 25,
        ...(payload && payload.more ? { cursor: state.nextCursor } : {}),
      }, signal);
      const recoveryErrors = {};
      if (!result.error && currentStatus.actorId) {
        for (const item of result.response.items) {
          if (item.syntheticPreview || item.readOnly) continue;
          const key = `${companyId}:${currentStatus.actorId}:${item.operationId}`;
          try {
            const body = readRecoveryRequest(key, companyId);
            if (body)
              yield put({
                type: 'learning/RECOVERY_REQUEST_STORED',
                companyId,
                payload: { key, body },
              });
          } catch (error) {
            recoveryErrors[item.operationId] =
              'A saved recovery request is invalid or unavailable. Replay remains blocked until the exact request can be recovered.';
          }
        }
      }
      if (!result.error)
        result = { ...result, response: { ...result.response, recoveryErrors } };
      success = 'learning/HISTORY_LOADED';
      break;
    }
    case 'learning/LOAD_ACTION':
      result = yield call(learningApi.getAction, payload, companyId);
      success = 'learning/ACTION_LOADED';
      break;
    case 'learning/IMPORT_SKILL':
    case 'learning/REPLACE_IMPORTED_SKILL': {
      const replace = type === 'learning/REPLACE_IMPORTED_SKILL';
      const candidate = replace
        ? state.skillImportReplacement
        : {
            file: payload,
            companyId,
            generation: currentStatus && currentStatus.generation,
          };
      if (
        !currentStatus ||
        currentStatus.learningEnabled !== false ||
        !candidate ||
        !candidate.file ||
        candidate.companyId !== companyId ||
        !Number.isSafeInteger(candidate.generation) ||
        candidate.generation !== currentStatus.generation ||
        (replace &&
          (typeof candidate.sha256 !== 'string' ||
            !/^[a-f0-9]{64}$/.test(candidate.sha256)))
      ) {
        result = {
          error: {
            message:
              'Refresh the disabled company catalog and select the baseline ZIP again.',
          },
        };
        break;
      }
      result = yield call(
        generativeAi.uploadAgentSkill,
        candidate.file,
        replace,
        {
          companyId,
          expectedGeneration: candidate.generation,
          expectedSha256: replace ? candidate.sha256 : null,
        }
      );
      if (
        result.error &&
        result.error.code === 'agent_skill_replace_confirmation_required' &&
        result.error.currentGeneration === candidate.generation &&
        typeof result.error.currentSha256 === 'string' &&
        /^[a-f0-9]{64}$/.test(result.error.currentSha256)
      ) {
        yield put({
          type: 'learning/SKILL_IMPORT_REPLACEMENT',
          companyId,
          payload: {
            ...candidate,
            name: result.error.skillName,
            sha256: result.error.currentSha256,
          },
        });
        return;
      }
      success = 'learning/SKILL_IMPORTED';
      break;
    }
    case 'learning/PREPARE_MIGRATION': {
      try {
        let retained = state.migrationRequest;
        if (!retained) {
          const form = state.migrationForm;
          if (
            currentStatus.learningEnabled !== false ||
            !Number.isSafeInteger(currentStatus.generation) ||
            !form.reason.trim() ||
            form.reason.length > 4000
          )
            throw new Error(
              'Migration setup requires learning off and a reason.'
            );
          const body = {
            action: payload,
            reason: form.reason,
            scope: { type: 'company' },
            expectedGeneration: currentStatus.generation,
          };
          if (payload === 'importMemory') {
            if (!form.file || form.file.size > 1024 * 1024)
              throw new Error('Select a baseline JSON file smaller than 1 MB.');
            const bundle = JSON.parse(yield call([form.file, form.file.text]));
            if (
              bundle.companyId !== companyId ||
              bundle.environment !== currentStatus.environment ||
              !Array.isArray(bundle.memories)
            )
              throw new Error(
                'The memory baseline must match the selected company and environment.'
              );
            body.skills = [];
            body.memories = bundle.memories;
          } else if (
            payload === 'learningEnabled' &&
            form.validated &&
            validUuid(form.baselineImportOperationId)
          ) {
            body.learningEnabled = true;
            body.baselineImportOperationId = form.baselineImportOperationId;
          } else
            throw new Error(
              'Validate the published baseline before preparing initial enablement.'
            );
          const created = yield call(
            learningApi.createPreparationSession,
            companyId
          );
          if (created.error) {
            result = created;
            break;
          }
          if (
            created.response.companyId !== companyId ||
            created.response.environment !== currentStatus.environment
          )
            throw new Error('Preparation company or environment changed.');
          retained = { preparationId: created.response.preparationId, body };
          yield put({
            type: 'learning/MIGRATION_REQUEST',
            companyId,
            payload: retained,
          });
        } else {
          const existing = yield call(
            learningApi.getAction,
            retained.preparationId,
            companyId
          );
          if (existing.error) {
            result = existing;
            break;
          }
          if (existing.response.status !== 'preparing') {
            const filled = existing.response.payload;
            if (
              !filled ||
              !filled._migrationSetup ||
              !Object.keys(retained.body).every((key) =>
                equals(retained.body[key], filled[key])
              )
            )
              throw new Error(
                'The prepared action differs from the retained migration request.'
              );
            result = existing;
            success = 'learning/ACTION_LOADED';
            break;
          }
        }
        result = yield call(
          learningApi.prepareMigration,
          retained.preparationId,
          companyId,
          retained.body
        );
        success = 'learning/ACTION_LOADED';
      } catch (error) {
        result = {
          error: {
            message:
              error.message || 'Unable to prepare the migration baseline.',
          },
        };
      }
      break;
    }
    case 'learning/PREPARE': {
      const operationId = payload ? payload.operationId : undefined;
      if (
        payload &&
        payload.operationId !== undefined &&
        (!operationId ||
          !state.items.some((item) => item.operationId === operationId))
      ) {
        result = {
          error: {
            message:
              'Select an operation from the current company history before preparing it.',
          },
        };
      } else {
        result = yield call(
          learningApi.createPreparationSession,
          companyId,
          operationId
        );
      }
      success = 'learning/PREPARATION_CREATED';
      break;
    }
    case 'learning/OPEN_RESOLUTION':
      if (
        !state.preparation ||
        state.preparation.companyId !== companyId ||
        state.preparation.originalStaffUserId !==
          (state.staffUserId ||
            (currentStatus &&
              (currentStatus.operatorId || currentStatus.actorId))) ||
        !(new Date(state.preparation.expiresAt).getTime() > Date.now())
      ) {
        result = {
          error: {
            message:
              'Create a current preparation session through your admin-panel company session.',
          },
        };
      } else {
        result = yield call(learningApi.openResolutionSession, {
          companyId,
          preparationId: state.preparation.preparationId,
        });
      }
      success = 'learning/RESOLUTION_OPENED';
      break;
    case 'learning/CONFIRM':
      if (
        !state.action ||
        state.action.companyId !== companyId ||
        state.action.originalStaffUserId !==
          (state.staffUserId ||
            (currentStatus &&
              (currentStatus.operatorId || currentStatus.actorId))) ||
        state.action.preparedActionId !== payload ||
        !state.action.actionDigest ||
        !(new Date(state.action.expiresAt).getTime() > Date.now())
      ) {
        result = {
          error: {
            message:
              'Reload the exact action using its original admin-panel operator and company.',
          },
        };
      } else {
        result = yield call(learningApi.confirmAction, {
          preparedActionId: state.action.preparedActionId,
          expectedActionDigest: state.action.actionDigest,
          companyId,
        });
      }
      success = 'learning/CONFIRMED';
      break;
    default:
      return;
  }
  if (result.error)
    yield put({
      type:
        type === 'learning/LOAD_HISTORY' &&
        [401, 403].includes(requestError(result.error).status)
          ? 'learning/READ_ACCESS_DENIED'
          : 'learning/ERROR',
      payload: {
        ...requestError(result.error, 'Unable to load learning records. Please retry.'),
        history: type === 'learning/LOAD_HISTORY',
        manualAction: type !== 'learning/LOAD_HISTORY',
      },
      companyId,
    });
  else {
    yield put({
      type: success,
      payload:
        success === 'learning/HISTORY_LOADED'
          ? {
              ...result.response,
              append: Boolean(payload && payload.more),
              syntheticRequested: Boolean(state.filters.syntheticPreview),
            }
          : result.response,
      companyId,
    });
    if (
      success === 'learning/SKILL_IMPORTED' ||
      success === 'learning/CONFIRMED'
    )
      yield put({ type: 'learning/LOAD_HISTORY', companyId });
  }
}

export function* learningRequest(action) {
  const companyId = yield select((root) => root.generativeAi.learning.companyId);
  const controller = new AbortController();
  let slow;
  try {
    if (action.type === 'learning/LOAD_HISTORY') {
      slow = yield fork(function* () {
        yield delay(5e3);
        yield put({ type: 'learning/HISTORY_SLOW', companyId });
      });
      yield race({
        request: call(performLearningRequest, action, controller.signal),
        cancelled: take('learning/CANCEL_HISTORY'),
      });
    } else {
      yield* performLearningRequest(action, controller.signal);
    }
  } finally {
    controller.abort();
    if (slow) yield cancel(slow);
    if (action.type === 'learning/LOAD_HISTORY' && (yield cancelled())) {
      yield put({ type: 'learning/HISTORY_INTERRUPTED', companyId });
    }
  }
}

function* learningDetailsRequest({ type, payload }) {
  if (type !== 'learning/VIEW_DETAILS') return;
  const state = yield select((root) => root.generativeAi.learning);
  const isAdmin = yield select(
    (root) => root.account && root.account.isAnAdmin
  );
  const selected = state.items.find((item) => item.operationId === payload);
  if (!isAdmin || !selected) {
    yield put({ type: 'learning/CLOSE_DETAILS' });
    return;
  }
  const companyId = state.companyId;
  const controller = new AbortController();
  try {
    const result = yield call(generativeAi.getLearningHistory, {
      companyId,
      operationId: payload,
      ...(selected.syntheticPreview ? { syntheticPreview: true } : {}),
    }, controller.signal);
    if (result.error) {
      const { status, message } = requestError(
        result.error,
        'Unable to load review details. Please retry.'
      );
      yield put({
        type:
          status === 401 || status === 403
            ? 'learning/READ_ACCESS_DENIED'
            : 'learning/DETAILS_FAILED',
        companyId,
        payload: { operationId: payload, status, message },
      });
    } else {
      const item = result.response.items.find(
        (entry) => entry.operationId === payload
      );
      yield put({
        type: item ? 'learning/DETAILS_LOADED' : 'learning/DETAILS_FAILED',
        companyId,
        payload: {
          operationId: payload,
          item,
          message: 'This learning record is no longer available.',
        },
      });
    }
  } finally {
    controller.abort();
  }
}

const learningSagas = [
  takeLatest(
    [
      'learning/VIEW_DETAILS',
      'learning/CLOSE_DETAILS',
      'learning/FILTERS',
      'learning/ENTER',
      'learning/SIGNED_OUT',
      'learning/READ_ACCESS_DENIED',
    ],
    learningDetailsRequest
  ),
  takeLatest(
    [
      'learning/LOAD_HISTORY',
      'learning/LOAD_ACTION',
      'learning/IMPORT_SKILL',
      'learning/PREPARE_MIGRATION',
      'learning/REPLACE_IMPORTED_SKILL',
      'learning/PREPARE',
      'learning/OPEN_RESOLUTION',
      'learning/CONFIRM',
      'learning/SETUP',
      'learning/PREPARE_RECOVERY',
      'learning/CONFIRM_RECOVERY',
      'learning/FILTERS',
      'learning/ENTER',
      'learning/SIGNED_OUT',
      'learning/READ_ACCESS_DENIED',
      'learning/END_VERIFICATION',
    ],
    learningRequest
  ),
];

export default learningSagas;
